/**
 * Phone Number Formatter
 * 
 * Handles progressive formatting of US phone numbers in XXX-XXX-XXXX format.
 * Provides real-time formatting as user types with intelligent cursor positioning.
 */

import { BaseFormatter, FormatContext, ExtendedFormattingResult } from './base-formatter';
import { FormattingOptions, ValidationResult } from '../types/formatting-types';
import { FormatValidators } from '../validation/format-validators';

/**
 * Phone number formatter implementing XXX-XXX-XXXX format
 * 
 * Features:
 * - Progressive formatting during typing
 * - Input validation (numeric only)
 * - Automatic dash insertion
 * - Cursor position preservation
 */
export class PhoneFormatter extends BaseFormatter {
  constructor() {
    super('phone', {
      defaultCountryCode: 'US'
    });
  }

  /**
   * Extract raw digits from formatted phone number
   */
  public extractRaw(formattedValue: string): string {
    return this.stripFormatting(formattedValue);
  }

  /**
   * Check if this formatter handles phone fields
   */
  public canHandle(fieldType: string): boolean {
    return fieldType === 'phone' || fieldType === 'telephone';
  }

  /**
   * Get the expected format pattern
   */
  public getFormatPattern(): string {
    return 'XXX-XXX-XXXX';
  }

  /**
   * Sanitize input to only allow numeric characters
   */
  protected sanitizeInput(value: string): string {
    // Remove all non-numeric characters
    return value.replace(/\D/g, '');
  }

  /**
   * Perform progressive phone number formatting
   */
  protected performFormat(context: FormatContext): ExtendedFormattingResult {
    const { rawValue, cursorPosition, isProgressive } = context;
    
    // Limit to 10 digits for US phone numbers
    const digits = rawValue.substring(0, 10);
    
    let formattedValue = '';
    let newCursorPosition = cursorPosition;

    // Progressive formatting based on digit count
    if (digits.length === 0) {
      formattedValue = '';
    } else if (digits.length <= 3) {
      // First 3 digits: just the digits
      formattedValue = digits;
    } else if (digits.length <= 6) {
      // First 6 digits: XXX-XXX
      formattedValue = `${digits.substring(0, 3)}-${digits.substring(3)}`;
    } else {
      // Full format: XXX-XXX-XXXX
      formattedValue = `${digits.substring(0, 3)}-${digits.substring(3, 6)}-${digits.substring(6)}`;
    }

    // Calculate new cursor position for progressive formatting
    if (isProgressive && cursorPosition !== undefined) {
      newCursorPosition = this.calculatePhoneCursorPosition(
        rawValue,
        digits,
        formattedValue,
        cursorPosition
      );
    }

    return {
      formattedValue,
      rawValue: digits,
      success: true,
      errors: [],
      newCursorPosition,
      wasModified: rawValue !== formattedValue
    };
  }

  /**
   * Validate phone number
   */
  protected performValidation(rawValue: string, options?: FormattingOptions): ValidationResult {
    return FormatValidators.validatePhone(rawValue);
  }

  /**
   * Check if input is a complete phone number
   */
  protected isCompleteInput(rawValue: string): boolean {
    return rawValue.length === 10;
  }

  /**
   * Check if character is valid for phone input (numeric only)
   */
  protected isValidInputCharacter(char: string): boolean {
    return /\d/.test(char);
  }

  /**
   * Remove formatting to get raw digits
   */
  protected stripFormatting(value: string): string {
    return value.replace(/\D/g, '');
  }

  /**
   * Calculate cursor position after phone formatting
   */
  private calculatePhoneCursorPosition(
    originalRaw: string,
    newRaw: string,
    formatted: string,
    originalCursor: number
  ): number {
    // If no digits added/removed, try to maintain relative position
    if (originalRaw.length === newRaw.length) {
      // Map cursor position accounting for dashes
      let digitCount = 0;
      for (let i = 0; i < formatted.length; i++) {
        const char = formatted[i];
        if (/\d/.test(char)) {
          digitCount++;
          if (digitCount === originalCursor + 1) {
            return i + 1;
          }
        }
      }
    }

    // Default: place cursor at end
    return formatted.length;
  }

  /**
   * Format phone number for display purposes
   * 
   * @param phoneNumber Raw phone number digits
   * @returns Formatted phone number string
   */
  public static formatPhoneForDisplay(phoneNumber: string): string {
    const formatter = new PhoneFormatter();
    const result = formatter.format(phoneNumber, {}, undefined, false);
    return result.formattedValue;
  }

  /**
   * Check if a string looks like a phone number
   * 
   * @param value String to check
   * @returns Whether it looks like a phone number
   */
  public static looksLikePhoneNumber(value: string): boolean {
    const digits = value.replace(/\D/g, '');
    return digits.length >= 7 && digits.length <= 11;
  }

  /**
   * Parse various phone number formats into clean digits
   * 
   * @param input Phone number in any format
   * @returns Clean digit string
   */
  public static parsePhoneNumber(input: string): string {
    // Remove common phone formatting
    let cleaned = input.replace(/[\s\-\(\)\+\.]/g, '');
    
    // Remove leading 1 for US numbers
    if (cleaned.length === 11 && cleaned.startsWith('1')) {
      cleaned = cleaned.substring(1);
    }
    
    // Only keep digits
    return cleaned.replace(/\D/g, '');
  }
}